import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import bcrypt from 'bcryptjs';
import { supabase } from '../supabaseClient';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: var(--dark);
`;

const LoginForm = styled.form`
  background: var(--card-bg);
  padding: 40px;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid var(--primary);
  border-radius: 8px;
  background: var(--gray);
  color: var(--light);
  font-size: 1rem;
`;

const Button = styled.button`
  background: var(--primary);
  color: var(--dark);
  border: none;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: background 0.3s ease;

  &:hover {
    background: var(--accent);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: var(--accent);
  font-size: 0.9rem;
  text-align: center;
`;

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // 1. Look up admin record by username (fetch email + hashed password)
      const { data, error: fetchError } = await supabase
        .from('admins')
        .select('email, password')
        .eq('username', username)
        .single();

      if (fetchError || !data) {
        setError('Invalid username or password');
        return;
      }

      // 2. Verify password against the stored bcrypt hash
      const isValid = await bcrypt.compare(password, data.password);
      if (!isValid) {
        setError('Invalid username or password');
        return;
      }

      // 3. Sign in via Supabase Auth so the client gets a real session JWT.
      //    Without this step the supabase client stays as "anon" role and
      //    storage uploads / RLS-protected tables will be rejected.
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: password,
      });

      if (authError) {
        setError('Authentication failed: ' + authError.message);
        return;
      }

      navigate('/admin/dashboard');

    } catch (err) {
      setError('Unexpected error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginForm onSubmit={handleSubmit}>
        <h2 style={{ color: 'var(--primary)', textAlign: 'center' }}>Admin Login</h2>
        <Input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          disabled={loading}
        />
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Button type="submit" disabled={loading}>
          {loading ? 'Logging in...' : 'Login'}
        </Button>
      </LoginForm>
    </LoginContainer>
  );
};

export default AdminLogin;